import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { Content } from 'src/contexts/ContentContext';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useReachProConversationEvents } from 'src/hooks/api/useReachProConversationEvents';
import { useReachProConversations } from 'src/hooks/api/useReachProConversations';
import { ContentId } from 'src/utils/constants/contentId';
import { CONVERSATION_SORT_TO_CID } from 'src/utils/constants/contentIdMaps';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import { getEventSort, getEventUiSort } from 'src/utils/eventQueryUtils';
import { EventSort } from 'src/WebApiController';

import * as styles from './ReachProSupportSidePanel.css';

export function ReachProSupportSidePanel() {
  const { reachProConversationId } = useParams();
  const navigate = useNavigate();
  const [isUnread, setIsUnread] = useState<boolean | undefined>(undefined);
  const [isSortDescending, setIsSortDescending] = useState(true);

  const { conversations, isLoading } = useReachProConversations();
  const { catalogEventsLookup } = useReachProConversationEvents(conversations);

  const filteredConversations = useMemo(() => {
    if (!conversations || conversations.length === 0) {
      return [];
    }

    const getTimestampMs = (c: { lastMessageTimestamp: string | null }) =>
      c.lastMessageTimestamp
        ? stringToUtcDate(c.lastMessageTimestamp).getTime()
        : 0;

    const isConversationUnread = (c: {
      lastMessageTimestamp: string | null;
      lastReadTimestamp: string | null;
    }) => {
      if (!c.lastMessageTimestamp) {
        return false;
      }
      if (!c.lastReadTimestamp) {
        return true;
      }
      const lastMsgMs = stringToUtcDate(c.lastMessageTimestamp).getTime();
      const lastReadMs = stringToUtcDate(c.lastReadTimestamp).getTime();
      return lastReadMs < lastMsgMs;
    };

    const filtered =
      isUnread === true
        ? conversations.filter(isConversationUnread)
        : conversations;

    return filtered.toSorted((a, b) => {
      const aTs = getTimestampMs(a);
      const bTs = getTimestampMs(b);
      return isSortDescending ? bTs - aTs : aTs - bTs;
    });
  }, [conversations, isUnread, isSortDescending]);

  const allCount = conversations?.length ?? 0;

  const onConversationClicked = (conversationId: string) => {
    navigate(`/reachpro-support/${conversationId}`);
  };

  return (
    <div className={styles.sidePanel}>
      <div className={styles.filterContainer}>
        <div className={styles.tabContainer}>
          <div
            className={
              styles.tab + (isUnread !== true ? ` ${styles.tabActive}` : '')
            }
            onClick={() => setIsUnread(undefined)}
          >
            <Content id={ContentId.All} />
            {` (${allCount})`}
          </div>
          <div
            className={
              styles.tab + (isUnread === true ? ` ${styles.tabActive}` : '')
            }
            onClick={() => setIsUnread(true)}
          >
            <Content id={ContentId.Unread} />
          </div>
        </div>
        <PosEnumSelect
          variant="textPlain"
          shape="none"
          style={{ paddingBottom: vars.spacing['xxs'] }}
          value={getEventUiSort(EventSort.Date, isSortDescending)}
          defaultValue={getEventUiSort(EventSort.Date, true)}
          onChange={(sort) => {
            const [, newIsSortDescending] = getEventSort(sort);
            if (newIsSortDescending !== isSortDescending) {
              setIsSortDescending(newIsSortDescending);
            }
          }}
          valueOptionsContent={CONVERSATION_SORT_TO_CID}
        />
      </div>
      {isLoading ? (
        <PosSpinner />
      ) : (
        <Virtuoso
          data={filteredConversations}
          itemContent={(_, conversation) => {
            const conversationId = conversation.conversationId.toString();
            const timestamp = conversation.lastMessageTimestamp
              ? stringToUtcDate(conversation.lastMessageTimestamp)
              : stringToUtcDate(conversation.createDate);

            const now = new Date();
            const isWithin24Hours =
              now.getTime() - timestamp.getTime() < 24 * 60 * 60 * 1000;

            const formatTimestamp = () => {
              const timeStr = timestamp.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
              });
              if (isWithin24Hours) {
                return timeStr;
              }
              const month = String(timestamp.getMonth() + 1).padStart(2, '0');
              const day = String(timestamp.getDate()).padStart(2, '0');
              const year = timestamp.getFullYear();
              return `${month}/${day}/${year} ${timeStr}`;
            };

            const seatingParts: string[] = [];
            if (conversation.section) seatingParts.push(conversation.section);
            if (conversation.row) seatingParts.push(`Row ${conversation.row}`);
            if (conversation.seatFrom || conversation.seatTo) {
              const seats =
                conversation.seatFrom && conversation.seatTo
                  ? `${conversation.seatFrom}-${conversation.seatTo}`
                  : conversation.seatFrom || conversation.seatTo;
              seatingParts.push(`Seats ${seats}`);
            }
            const seatingDisplay =
              seatingParts.length > 0 ? seatingParts.join(' • ') : null;

            const listingOrSaleId = conversation.stubHubSaleId
              ? { type: 'sale' as const, id: conversation.stubHubSaleId }
              : conversation.stubHubListingId
                ? {
                    type: 'listing' as const,
                    id: conversation.stubHubListingId,
                  }
                : null;

            const eventInfo =
              conversation.eventId && catalogEventsLookup
                ? catalogEventsLookup[conversation.eventId.toString()]
                : null;

            return (
              <div
                className={clsx(
                  styles.conversationItem,
                  reachProConversationId === conversationId &&
                    styles.conversationItemActive
                )}
                onClick={() => onConversationClicked(conversationId)}
              >
                <div className={styles.conversationHeader}>
                  <span className={styles.conversationTitle}>
                    {conversation.topic || (
                      <Content id={ContentId.SupportConversation} />
                    )}
                  </span>
                  <span className={styles.conversationTime}>
                    {formatTimestamp()}
                  </span>
                </div>
                {eventInfo && (
                  <div className={styles.eventInfoRow}>
                    <span className={styles.metadataValue}>
                      {eventInfo.performerName || eventInfo.eventName}
                    </span>
                    {eventInfo.venueName && (
                      <span className={styles.metadataLabel}>
                        {' • '}
                        {eventInfo.venueName}
                      </span>
                    )}
                    {eventInfo.eventStartDate && (
                      <span className={styles.metadataLabel}>
                        {' • '}
                        {new Date(eventInfo.eventStartDate).toLocaleDateString(
                          [],
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          }
                        )}
                      </span>
                    )}
                  </div>
                )}
                {(listingOrSaleId || seatingDisplay) && (
                  <div className={styles.conversationMetadata}>
                    {listingOrSaleId && (
                      <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>
                          <Content
                            id={
                              listingOrSaleId.type === 'sale'
                                ? ContentId.Sale
                                : ContentId.Listing
                            }
                          />
                          :
                        </span>
                        <span className={styles.metadataValue}>
                          {listingOrSaleId.id}
                        </span>
                      </div>
                    )}
                    {seatingDisplay && (
                      <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>
                          <Content id={ContentId.Seating} />:
                        </span>
                        <span className={styles.metadataValue}>
                          {seatingDisplay}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.conversationLastMessage}>
                  {conversation.hasUnreadMessages && (
                    <span className={styles.unreadBadge}>●</span>
                  )}
                  <span className={styles.conversationLastMessageText}>
                    {conversation.lastMessageSnippet || (
                      <Content id={ContentId.NoMessagesYet} />
                    )}
                  </span>
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
}
